import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

const CATEGORIES = [
  { slug: "administratif", label: "Administratif" },
  { slug: "finance", label: "Finance" },
  { slug: "sante", label: "Santé" },
  { slug: "auto", label: "Auto" },
  { slug: "famille", label: "Famille" },
  { slug: "vie-pratique", label: "Vie Pratique" },
];

const PILLAR_GUIDES = [
  { slug: "guide-demarches-administratives-maroc", label: "Guide Admin" },
  { slug: "guide-visa-maroc", label: "Guide Visa" },
  { slug: "guide-sante-maroc", label: "Guide Santé" },
  { slug: "guide-auto-transport-maroc", label: "Guide Auto" },
  { slug: "guide-famille-maroc", label: "Guide Famille" },
];

export default function Navigation() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catsOpen, setCatsOpen] = useState(false);
  const [guidesOpen, setGuidesOpen] = useState(false);
  const location = useLocation();

  return (
    <header
      data-testid="main-navigation"
      className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center flex-shrink-0"
            aria-label="Maroc Pratique — Accueil"
          >
            {/* Mobile : icône seule */}
            <img
              src="/brand/icon-color.svg"
              alt="Maroc Pratique"
              className="h-10 w-10 sm:hidden"
              width="40"
              height="40"
            />
            {/* Desktop : logo horizontal — viewBox recadré 318×62, ratio 5.13:1 */}
            <img
              src="/brand/logo-horizontal-color.svg"
              alt="Maroc Pratique"
              className="hidden sm:block h-10 w-auto"
              width="205"
              height="40"
              fetchpriority="high"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            <Link
              to="/"
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                location.pathname === "/"
                  ? "text-blue-600 bg-blue-50"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
              }`}
            >
              Accueil
            </Link>

            {/* Categories dropdown */}
            <div className="relative">
              <button
                onClick={() => setCatsOpen(!catsOpen)}
                data-testid="categories-dropdown-btn"
                aria-haspopup="true"
                aria-expanded={catsOpen}
                className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-colors"
              >
                Catégories
                <ChevronDown className={`w-4 h-4 transition-transform ${catsOpen ? "rotate-180" : ""}`} />
              </button>
              {catsOpen && (
                <div
                  className="absolute top-full left-0 mt-1 w-56 bg-white border border-slate-200 rounded-xl shadow-lg py-1 z-50"
                  data-testid="categories-dropdown"
                >
                  {CATEGORIES.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/categorie/${cat.slug}`}
                      onClick={() => setCatsOpen(false)}
                      data-testid={`nav-cat-${cat.slug}`}
                      className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                    >
                      {cat.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Guides Piliers dropdown */}
            <div className="relative">
              <button
                onClick={() => setGuidesOpen(!guidesOpen)}
                data-testid="guides-dropdown-btn"
                aria-haspopup="true"
                aria-expanded={guidesOpen}
                className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-colors"
              >
                Guides
                <ChevronDown className={`w-4 h-4 transition-transform ${guidesOpen ? "rotate-180" : ""}`} />
              </button>
              {guidesOpen && (
                <div
                  className="absolute top-full left-0 mt-1 w-60 bg-white border border-slate-200 rounded-xl shadow-lg py-1 z-50"
                  data-testid="guides-dropdown"
                >
                  {PILLAR_GUIDES.map((guide) => (
                    <Link
                      key={guide.slug}
                      to={`/pilier/${guide.slug}`}
                      onClick={() => setGuidesOpen(false)}
                      data-testid={`nav-guide-${guide.slug}`}
                      className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                    >
                      {guide.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-btn"
            aria-label={mobileOpen ? "Fermer le menu" : "Ouvrir le menu de navigation"}
            aria-expanded={mobileOpen}
            className="lg:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div
            data-testid="mobile-menu"
            className="lg:hidden border-t border-slate-200 py-3 space-y-1"
          >
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="block px-3 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Accueil
            </Link>
            <p className="px-3 pt-2 pb-1 text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Catégories
            </p>
            {CATEGORIES.map((cat) => (
              <Link
                key={cat.slug}
                to={`/categorie/${cat.slug}`}
                onClick={() => setMobileOpen(false)}
                data-testid={`mobile-nav-cat-${cat.slug}`}
                className="block px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-700"
              >
                {cat.label}
              </Link>
            ))}
            <p className="px-3 pt-2 pb-1 text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Guides Pratiques
            </p>
            {PILLAR_GUIDES.map((guide) => (
              <Link
                key={guide.slug}
                to={`/pilier/${guide.slug}`}
                onClick={() => setMobileOpen(false)}
                data-testid={`mobile-nav-guide-${guide.slug}`}
                className="block px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-700"
              >
                {guide.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </header>
  );
}
